// SignalTower — the Contact-scene landmark. A tall tower: cylinder
// 0.08r x 1h, antenna 0.02r x 0.4h on top, with a small pulsing point
// at the tip. Strong point light at the top (intensity 1.5, color
// mint, distance 3) — it's a beacon.

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

const COLOR_TOWER = '#b8a572'; // gold
const COLOR_ANTENNA = '#7fb89f'; // mint
const COLOR_LIGHT = '#7fb89f'; // mint

export interface SignalTowerProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  isActive?: boolean;
  isHovered?: boolean;
  groupRef?: React.MutableRefObject<Group | null>;
}

function TowerBeam({
  from,
  to,
  radius = 0.014,
  color = COLOR_TOWER,
}: {
  from: [number, number, number];
  to: [number, number, number];
  radius?: number;
  color?: string;
}) {
  const transform = useMemo(() => {
    const start = new THREE.Vector3(...from);
    const end = new THREE.Vector3(...to);
    const direction = end.clone().sub(start);
    return {
      midpoint: start.clone().add(end).multiplyScalar(0.5),
      length: direction.length(),
      quaternion: new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        direction.normalize(),
      ),
    };
  }, [from, to]);

  return (
    <mesh position={transform.midpoint} quaternion={transform.quaternion}>
      <cylinderGeometry args={[radius, radius, transform.length, 5]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.12}
        roughness={0.66}
        flatShading
      />
    </mesh>
  );
}

export function SignalTower({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  groupRef,
}: SignalTowerProps) {
  const tipRef = useRef<Mesh | null>(null);
  const lightRef = useRef<THREE.PointLight | null>(null);
  const t0 = useRef(performance.now() / 1000);

  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.12 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);
  const legs = useMemo(() => {
    return [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].map((angle) => ({
      from: [Math.cos(angle) * 0.19, 0.1, Math.sin(angle) * 0.19] as [number, number, number],
      to: [Math.cos(angle) * 0.052, 1.12, Math.sin(angle) * 0.052] as [number, number, number],
    }));
  }, []);
  const braces = useMemo(() => {
    const output: Array<{
      from: [number, number, number];
      to: [number, number, number];
    }> = [];
    const levels = [
      { y: 0.26, r: 0.168 },
      { y: 0.52, r: 0.132 },
      { y: 0.78, r: 0.096 },
      { y: 1.02, r: 0.063 },
    ];
    levels.forEach((level, levelIndex) => {
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const b = ((i + 1) / 3) * Math.PI * 2;
        output.push({
          from: [Math.cos(a) * level.r, level.y, Math.sin(a) * level.r],
          to: [Math.cos(b) * level.r, level.y, Math.sin(b) * level.r],
        });
        if (levelIndex < levels.length - 1) {
          const next = levels[levelIndex + 1];
          output.push({
            from: [Math.cos(a) * level.r, level.y, Math.sin(a) * level.r],
            to: [Math.cos(b) * next.r, next.y, Math.sin(b) * next.r],
          });
        }
      }
    });
    return output;
  }, []);

  useFrame(() => {
    const t = performance.now() / 1000 - t0.current;
    // Pulse on a 1.6s cycle.
    const k = (t * 2 * Math.PI) / 1.6;
    const pulse = 0.5 + 0.5 * Math.sin(k);
    if (tipRef.current) {
      const s = 1 + pulse * 0.5;
      tipRef.current.scale.setScalar(s);
    }
    if (lightRef.current) {
      lightRef.current.intensity = 1.0 + pulse * 1.0; // 1.0..2.0
    }
  });

  return (
    <group
      ref={groupRef as React.MutableRefObject<Group>}
      name="landmark-signal-tower"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {/* Stepped footing gives the lattice a deliberate terrain connection. */}
      <mesh name="tower-base" position={[0, 0.045, 0]}>
        <cylinderGeometry args={[0.2, 0.24, 0.09, 8]} />
        <meshStandardMaterial color={COLOR_TOWER} roughness={0.85} flatShading />
      </mesh>
      <mesh
        name="tower-contact-shadow"
        position={[0, 0.002, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        renderOrder={1}
        raycast={() => null}
      >
        <circleGeometry args={[0.27, 10]} />
        <meshBasicMaterial
          color="#07151d"
          transparent
          opacity={0.25}
          depthWrite={false}
          polygonOffset
          polygonOffsetFactor={-1}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh name="tower-base-cap" position={[0, 0.1, 0]}>
        <cylinderGeometry args={[0.17, 0.2, 0.03, 8]} />
        <meshStandardMaterial color={COLOR_ANTENNA} roughness={0.65} flatShading />
      </mesh>

      {legs.map((leg, index) => (
        <TowerBeam key={`leg-${index}`} from={leg.from} to={leg.to} radius={0.022} />
      ))}
      {braces.map((brace, index) => (
        <TowerBeam
          key={`brace-${index}`}
          from={brace.from}
          to={brace.to}
          radius={0.008}
          color={index % 2 === 0 ? COLOR_ANTENNA : COLOR_TOWER}
        />
      ))}

      {[0.26, 0.52, 0.78, 1.02].map((y, index) => (
        <mesh
          key={y}
          name={`tower-platform-${index}`}
          position={[0, y, 0]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <torusGeometry args={[0.16 - index * 0.032, 0.009, 4, 18]} />
          <meshStandardMaterial
            color={index === 3 ? COLOR_LIGHT : COLOR_TOWER}
            emissive={index === 3 ? COLOR_LIGHT : COLOR_TOWER}
            emissiveIntensity={index === 3 ? 0.45 : 0.1}
            roughness={0.62}
          />
        </mesh>
      ))}

      {/* Needle mast and beacon. */}
      <mesh
        name="tower-antenna"
        position={[0, 1.34, 0]}
        userData={{ landmarkType: 'signal_tower' }}
      >
        <cylinderGeometry args={[0.014, 0.028, 0.48, 6]} />
        <meshStandardMaterial
          color={COLOR_ANTENNA}
          emissive={COLOR_ANTENNA}
          emissiveIntensity={0.4}
          roughness={0.5}
          flatShading
        />
      </mesh>

      {/* Antenna tip — small pulsing ball. */}
      <mesh
        ref={tipRef}
        name="tower-tip"
        position={[0, 1.6, 0]}
        userData={{ landmarkType: 'signal_tower' }}
      >
        <sphereGeometry args={[0.05, 10, 8]} />
        <meshStandardMaterial
          color={COLOR_LIGHT}
          emissive={COLOR_LIGHT}
          emissiveIntensity={1.0}
        />
      </mesh>

      {/* Strong point light at the top — this is the beacon. */}
      <pointLight
        ref={lightRef}
        color={COLOR_LIGHT}
        intensity={1.5}
        distance={3}
        position={[0, 1.6, 0]}
      />

      {/* Secondary lower light for fill. */}
      <pointLight
        color={COLOR_TOWER}
        intensity={0.4}
        distance={1.5}
        position={[0, 0.55, 0]}
      />
    </group>
  );
}

export default SignalTower;
